import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import type {
  PortalDraftValue,
  PortalRequestResponse,
  PortalSessionResponse,
  PortalTableRows,
} from "@ledgerbox/domain"

import { PortalApiError } from "./api"
import { usePortalApi } from "./api-context"
import {
  createDraftSaver,
  draftSavedAt,
  withItemDraft,
  type DraftStatus,
  type ItemDraft,
} from "./item-draft"

/* The draft of an answer, wired to the page: the field's own state and
   saver, and the session copy a row that mounts again reads from. */

/**
 * Puts a saved draft into the session, so a row that mounts again, or a
 * view the client comes back to, starts from the newest text. The session
 * is read through a ref: a save lands after the render that started it.
 */
export function useDraftMemory(
  session: PortalSessionResponse,
  onSessionChange: (session: PortalSessionResponse) => void
) {
  const latest = useRef(session)
  latest.current = session
  return (itemId: string, draft: ItemDraft) =>
    onSessionChange(withItemDraft(latest.current, itemId, draft))
}

type Item = PortalRequestResponse["items"][number]

/** How one kind reads its answer and its draft: as text, or as rows. */
type DraftShape<V extends PortalDraftValue> = {
  read(value: unknown): V | undefined
  empty: V
}

const textShape: DraftShape<string> = {
  read: (value) => z.string().safeParse(value).data,
  empty: "",
}

const rowsShape: DraftShape<PortalTableRows> = {
  read: (value) => z.array(z.array(z.string())).safeParse(value).data,
  empty: [],
}

/**
 * What a client is typing, kept on the server a moment after each pause.
 * The field starts from the saved draft, so another device or a row opened
 * again shows the same words. `settle` runs before the answer is sent, so
 * the answer is always the last write.
 */
function useItemDraft<V extends PortalDraftValue>(
  item: Item,
  onDraftSaved: (draft: ItemDraft) => void,
  shape: DraftShape<V>
) {
  const api = usePortalApi()
  const answered = shape.read(item.answer?.value) ?? shape.empty
  const drafted = shape.read(item.draft?.value)
  const [value, setValue] = useState<V>(drafted ?? answered)
  const [status, setStatus] = useState<DraftStatus<V>>(
    drafted === undefined ? { kind: "idle" } : { kind: "saved", value: drafted }
  )
  const latest = useRef({ item, onDraftSaved, seen: null as string | null })
  latest.current.item = item
  latest.current.onDraftSaved = onDraftSaved
  const [saver] = useState(() =>
    createDraftSaver<V>({
      save: (next) => {
        const { item: current, seen } = latest.current
        return api.saveItemDraft(current.itemId, {
          value: next,
          savedAt: draftSavedAt(Date.now(), [
            current.draft?.savedAt,
            current.answer?.answeredAt,
            seen,
          ]),
        })
      },
      onDraft: (draft) => {
        latest.current.seen = draft.savedAt
        latest.current.onDraftSaved(draft)
      },
      onStatus: setStatus,
      errorMessage: (error) =>
        error instanceof PortalApiError
          ? error.message
          : "Your answer is not saved yet. Check your connection.",
    })
  )

  // A row that closes saves what is waiting instead of dropping it.
  useEffect(() => () => void saver.flush(), [saver])
  // A new answer, from this device or the firm, replaces the field. Keyed
  // by its text, since a table's rows are a new array on every render.
  const answeredKey = JSON.stringify(answered)
  useEffect(
    () => setValue(shape.read(latest.current.item.draft?.value) ?? answered),
    [answeredKey]
  )

  return {
    value,
    answered,
    status,
    change(next: V) {
      setValue(next)
      saver.schedule(next)
    },
    settle: () => saver.settle(),
  }
}

/** The draft of a text, date or amount answer. */
export const useTextDraft = (
  item: Item,
  onDraftSaved: (draft: ItemDraft) => void
) => useItemDraft(item, onDraftSaved, textShape)

/** The draft of a table's rows. */
export const useTableDraft = (
  item: Item,
  onDraftSaved: (draft: ItemDraft) => void
) => useItemDraft(item, onDraftSaved, rowsShape)
